import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Search, Filter, RefreshCw, ExternalLink, Loader2 } from 'lucide-react';
import api from '../services/api';
import { userAPI } from '../services/api';
import { formatDateTime } from '../utils/timezone';
import Paginator from '../components/ui/Paginator';
import { toast } from 'sonner';

const TICKET_TYPES = [
  { value: '', label: 'All Activity' },
  { value: 'incident', label: 'Incidents' },
  { value: 'sr', label: 'Service Requests' },
  { value: 'cr', label: 'Change Requests' },
  { value: 'auth', label: 'Login / Logout' },
  { value: 'user', label: 'User Management' },
  { value: 'application', label: 'Applications / Systems' },
  { value: 'category', label: 'Categories' },
  { value: 'delegation', label: 'Delegations' },
  { value: 'sla_policy', label: 'SLA Global Defaults' },
  { value: 'sla_set', label: 'SLA Policy Sets' },
  { value: 'sla_bh', label: 'Business Hours' },
  { value: 'sla_holiday', label: 'Holidays' },
  { value: 'escalation_policy', label: 'Escalation Policies' },
  { value: 'escalation_rule', label: 'Escalation Rules' },
  { value: 'team', label: 'Apps & Teams Mapping' },
  { value: 'shift', label: 'Shift Calendar' },
  { value: 'shift_assign', label: 'Shift Assignments' },
];

const FIELD_LABELS = {
  created: 'Created',
  updated: 'Updated',
  deleted: 'Deleted',
  deactivated: 'Deactivated / Stopped',
  reactivated: 'Reactivated',
  status: 'Status Changed',
  assigned_to: 'Assignment Changed',
  priority: 'Priority / Urgency Changed',
  comment: 'Comment Added',
  work_note: 'Work Note Added',
  resolution_summary: 'Resolution Summary Updated',
  approved: 'Approved',
  rejected: 'Rejected',
  member_added: 'Member Added',
  member_removed: 'Member Removed',
  password_reset: 'Password Reset',
  dept_created: 'Department Created',
  dept_updated: 'Department Updated',
  dept_deleted: 'Department Deleted',
  sla_mode_changed: 'SLA Mode Changed',
  round_robin_changed: 'Round Robin Changed',
  auto_assign_changed: 'Auto-Assign Changed',
  escalation_toggled: 'Escalation Toggled',
  auto_close_changed: 'Auto-Close Changed',
  sla_override_changed: 'SLA Mode Override Changed',
  login: 'Login',
  login_mfa: 'Login (MFA)',
  mfa_setup: 'MFA Setup',
  logout: 'Logout',
};

const FIELD_COLORS = {
  created: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  updated: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  deleted: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  status: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  assigned_to: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  priority: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  comment: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  work_note: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  resolution_summary: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  rejected: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  login: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  login_mfa: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  mfa_setup: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  logout: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400',
  deactivated: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400',
  reactivated: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  sla_mode_changed: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  round_robin_changed: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  auto_assign_changed: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  escalation_toggled: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  auto_close_changed: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  sla_override_changed: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  dept_created: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  dept_updated: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  dept_deleted: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  member_added: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  member_removed: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
};

/** Smart helper to render the Change column with meaningful text */
function renderChangeValue(log) {
  const field = log.field_changed || '';
  const old = log.old_value || '';
  const nv  = log.new_value  || '';

  // Actions where old_value = what was removed/deactivated
  const showOld = ['deleted', 'dept_deleted', 'deactivated', 'member_removed'].includes(field);
  if (showOld) return old || nv || '—';

  // SLA mode — handle legacy "True"/"False" values from old records
  if (field === 'sla_mode_changed') {
    const toLabel = (v) => {
      if (!v || v === 'None' || v === 'null') return '24/7 Calendar';
      if (v === 'True' || v === 'true' || v === 'Business Hours Only' || v === 'Business Hours') return 'Business Hours';
      if (v === 'False' || v === 'false' || v === '24/7 Calendar Time' || v === '24/7 Calendar') return '24/7 Calendar';
      return v;
    };
    const o = toLabel(old), n = toLabel(nv);
    return o && n && o !== n ? `${o} → ${n}` : (n || o || '—');
  }

  // If both old and new exist and differ — show arrow
  if (old && nv && old !== nv) return `${old} → ${nv}`;

  // Otherwise show whichever is present
  return nv || old || '—';
}

export default function AuditLogsPage() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState({
    ticket_type: '',
    changed_by: '',
    date_from: '',
    date_to: '',
    search: '',
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (filters.ticket_type) params.ticket_type = filters.ticket_type;
      if (filters.changed_by) params.changed_by = filters.changed_by;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.search) params.search = filters.search;

      const res = await api.get('/tickets/audit-logs/', { params });
      const data = res.data?.results || res.data || [];
      setLogs(data);
      setTotalCount(res.data?.count || data.length);
    } catch {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    userAPI.list({ page_size: 500 }).then(r => setUsers(r.data?.results || r.data || [])).catch(() => {});
  }, []);

  const handleFilterChange = (key, val) => {
    setFilters(prev => ({ ...prev, [key]: val }));
    setPage(1);
  };

  const getTicketUrl = (type, id) => {
    if (!id) return null;
    return `/ticket/${type}/${id}`;
  };

  const typeLabel = (t) => ({
    incident: 'INC', sr: 'SR', cr: 'CR', auth: 'AUTH',
    user: 'USER', application: 'APP', category: 'CAT', delegation: 'DELEG',
    sla_policy: 'SLA', sla_set: 'SLA-SET', sla_bh: 'BH', sla_holiday: 'HOLIDAY',
    escalation_policy: 'ESC', escalation_rule: 'ESC-R', team: 'TEAM', shift: 'SHIFT',
    shift_assign: 'SHIFT-A',
  }[t] || t?.toUpperCase().slice(0, 6));

  const typeBadge = (t) => {
    if (['incident'].includes(t)) return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400';
    if (['sr'].includes(t)) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    if (['cr'].includes(t)) return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
    if (['auth', 'user'].includes(t)) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
    if (['sla_policy', 'sla_set', 'sla_bh', 'sla_holiday'].includes(t)) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    if (['escalation_policy', 'escalation_rule'].includes(t)) return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
    if (['team', 'shift', 'shift_assign'].includes(t)) return 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400';
    if (['application', 'category', 'delegation'].includes(t)) return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';
    return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
  };

  // totalPages is computed for reference; Paginator calculates internally from total + pageSize

  return (
    <div data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-sky-500" /> Audit Logs
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Complete activity trail — who changed what and when across all tickets
          </p>
        </div>
        <button onClick={fetchLogs} className="flex items-center gap-2 text-sm text-slate-500 hover:text-sky-600 transition-colors" data-testid="refresh-audit-btn">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-4 mb-5">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-slate-600 dark:text-slate-400">
          <Filter className="w-4 h-4" /> Filters
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Search */}
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by field or value..."
              value={filters.search}
              onChange={e => handleFilterChange('search', e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 border dark:border-slate-700 rounded-xl text-sm dark:bg-slate-900 dark:text-white"
              data-testid="audit-search"
            />
          </div>
          {/* Ticket Type */}
          <select
            value={filters.ticket_type}
            onChange={e => handleFilterChange('ticket_type', e.target.value)}
            className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white"
            data-testid="audit-type-filter"
          >
            {TICKET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          {/* Date From */}
          <input
            type="date"
            value={filters.date_from}
            onChange={e => handleFilterChange('date_from', e.target.value)}
            className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white"
            data-testid="audit-date-from"
          />
          {/* Date To */}
          <input
            type="date"
            value={filters.date_to}
            onChange={e => handleFilterChange('date_to', e.target.value)}
            className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white"
            data-testid="audit-date-to"
          />
        </div>
      </div>

      {/* Count */}
      <div className="text-xs text-slate-500 mb-3">{totalCount.toLocaleString()} entries found</div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No audit entries found</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900 border-b dark:border-slate-700">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Ticket / User</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Action</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Change</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">By</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">When</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {logs.map((log, i) => (
                    <tr key={log.id || i} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                      {/* Ticket */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${typeBadge(log.ticket_type)}`}>
                            {typeLabel(log.ticket_type)}
                          </span>
                          <span className="font-mono text-xs text-slate-600 dark:text-slate-300">
                            {log.ticket_type === 'auth' ? log.ticket_number : (log.ticket_number || '—')}
                          </span>
                        </div>
                      </td>
                      {/* Action */}
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2.5 py-1 rounded-lg font-medium ${FIELD_COLORS[log.field_changed] || 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'}`}>
                          {FIELD_LABELS[log.field_changed] || log.field_changed?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      {/* Change */}
                      <td className="px-4 py-3 max-w-xs">
                        {log.field_changed === 'status' && log.old_value && log.new_value ? (
                          <div className="flex items-center gap-1.5 text-xs">
                            <span className="line-through text-rose-400 capitalize">{log.old_value.replace(/_/g, ' ')}</span>
                            <span className="text-slate-400">→</span>
                            <span className="text-emerald-600 dark:text-emerald-400 font-medium capitalize">{log.new_value.replace(/_/g, ' ')}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-600 dark:text-slate-300 truncate block max-w-[200px]">{renderChangeValue(log)}</span>
                        )}
                      </td>
                      {/* By */}
                      <td className="px-4 py-3">
                        <span className="text-xs text-slate-700 dark:text-slate-300">{log.changed_by_name || 'System'}</span>
                      </td>
                      {/* When */}
                      <td className="px-4 py-3">
                        <span className="text-xs text-slate-500 whitespace-nowrap">{log.changed_at ? formatDateTime(log.changed_at) : '—'}</span>
                      </td>
                      {/* Link */}
                      <td className="px-4 py-3">
                        {log.ticket_id && log.ticket_type && log.ticket_type !== 'auth' && (
                          <button
                            onClick={() => navigate(getTicketUrl(log.ticket_type, log.ticket_id))}
                            className="text-sky-500 hover:text-sky-700 transition-colors"
                            title="View ticket"
                            data-testid={`view-ticket-${log.id}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-700">
              {logs.map((log, i) => (
                <div key={log.id || i} className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${typeBadge(log.ticket_type)}`}>
                        {typeLabel(log.ticket_type)}
                      </span>
                      <span className="font-mono text-xs text-slate-600 dark:text-slate-300">{log.ticket_number || '—'}</span>
                    </div>
                    <span className="text-xs text-slate-400">{log.changed_at ? formatDateTime(log.changed_at) : '—'}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-lg font-medium ${FIELD_COLORS[log.field_changed] || 'bg-slate-100 text-slate-600'}`}>
                      {FIELD_LABELS[log.field_changed] || log.field_changed?.replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs text-slate-500">by</span>
                    <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{log.changed_by_name || 'System'}</span>
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-300">{renderChangeValue(log)}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-4">
        <Paginator
          total={totalCount}
          page={page}
          pageSize={pageSize}
          onPage={(p) => setPage(p)}
          onPageSize={(s) => { setPageSize(s); setPage(1); }}
        />
      </div>
    </div>
  );
}
